import { useTranslation } from "react-i18next";
import { useMediaQuery } from "@mui/material";
import { useTheme } from "@mui/material/styles";

import { FormDateRange } from "@/components/organisms/FilterDropdown/components/DateRangeForm/DateRangeForm";
import usePaginationFromEndpoint, {
    usePaginationFromEndpointProps,
} from "@/components/templates/PaginatedList/usePaginationFromEndpoint";
import useSearchFromEndpoint, {
    useSearchFromEndpointProps,
} from "@/components/templates/SearchList/useSearchFromEndpoint";
import { defaultDateFormat } from "@/core/date";
import { FieldPath } from "@/core/fieldPath";
import { claimApi } from "@/shared/apis";
import { AllClaimQueryParameters, ClaimCategoryQueryParameters, searchableClaimMapper } from "@/shared/apis/claimApi";
import { IFilterItem, LocalFilterType } from "@/shared/contexts/localFilterContext";
import { Claim, ClaimPagination, ClaimSortProperty, SearchableClaim, SortOrder } from "@/shared/domain";
import { useGlobalFilter } from "@/shared/hooks";
import { useLocalFilter } from "@/shared/hooks/useLocalFilter";
import { usePolicyholders } from "@/shared/hooks/usePolicyholders";

type useClaimsQueriesProps = {
    tableMode: boolean;
    localFilters: IFilterItem[];
    useClaimsPageQuery: ({ defaultCategory }) => usePaginationFromEndpointProps<Claim, ClaimSortProperty>;
    useClaimsSearchQuery: ({ textSearch, sortProperty }) => useSearchFromEndpointProps<Claim>;
};

export const getClaimSearchFilters = (
    withPolicyholder = false,
    withCircumstance = false,
): FieldPath<SearchableClaim>[] => {
    const claimSearchFilters: FieldPath<SearchableClaim>[] = [
        "referenceString",
        "description",
        "person",
        "objectType.name",
        "objectDescription",
        "damageEventDateString",
        "brokerState.name",
        "victim",
    ];

    claimSearchFilters.pushIf(withPolicyholder, "policyHolder");

    claimSearchFilters.pushIf(withCircumstance, "circumstance.name");

    return claimSearchFilters;
};

export const useClaimsQueries = ({ applyFilter = false }: { applyFilter?: boolean }): useClaimsQueriesProps => {
    const { t } = useTranslation();
    const theme = useTheme();
    const { isProEnvironment } = useGlobalFilter();
    const { policyholders } = usePolicyholders({
        checkRight: (a) => a.canViewClaims,
    });
    const { isFilterApplied, getFilterValue } = useLocalFilter();
    const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));
    const tableMode = isProEnvironment && !isSmallScreen;
    const localFilters = [
        {
            type: LocalFilterType.DATE_RANGE,
            label: t("Common.TimeRange"),
            id: "claimDateRange",
            objectType: "claim",
            count: (values: FormDateRange) => {
                return values ? 1 : 0;
            },
            isNull: (values: FormDateRange) => {
                return !values.dateFrom && !values.dateUntil;
            },
            valueDisplay: (values: FormDateRange) => {
                return [defaultDateFormat(values.dateFrom), defaultDateFormat(values.dateUntil)]
                    .filter((data) => !!data)
                    .join(" - ");
            },
        } as IFilterItem,
    ];

    const appliedFilters = localFilters
        .filter((filter) => isFilterApplied(filter.id))
        .map((filter) => getFilterValue(filter.id));

    const useClaimsPageQuery = ({ defaultCategory }) => {
        return usePaginationFromEndpoint<Claim, ClaimPagination, ClaimCategoryQueryParameters, ClaimSortProperty>({
            objectType: "claim",
            tableMode,
            endPoint: claimApi.getClaims,
            defaultCategory,
            policyholdersIdList: policyholders,
            filters: applyFilter ? appliedFilters : [],
            defaultSorting: { property: "damageEventDate", order: SortOrder.Descending },
        });
    };

    const useClaimsSearchQuery = ({ textSearch, sortProperty }) => {
        const filters = getClaimSearchFilters(!isProEnvironment, tableMode);

        const filterClaims = (results: Claim[]) =>
            searchableClaimMapper(results).filterBy(textSearch, filters).sortBy(sortProperty) || [];

        return useSearchFromEndpoint<Claim, ClaimPagination, AllClaimQueryParameters>(
            "claims",
            claimApi.getClaimsLazy,
            policyholders,
            filterClaims,
        );
    };

    return {
        tableMode,
        localFilters,
        useClaimsPageQuery,
        useClaimsSearchQuery,
    };
};
